import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { forkJoin, from, Observable, of } from "rxjs";
import { filter, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import {
  AbstractCirculationsService,
  CirculationId,
  EntityId,
  IMyTask,
  IReorderStepsData,
  IStepForwardData,
  IStepInit,
  IStepOverrideForwardData,
  IStepResultData,
  IStepTypeProviderDescription,
  Step,
  StepId
} from "src/app/interfaces";
import { LogClass, Logger, LogMethod } from "src/app/logger";
import { StepService } from "src/app/services";
import {
  selectCurrentStep,
  selectStep,
  selectStepsOfCirculation
} from "../state";
import { StepActions, StepAPIActions } from "../state/actions";

@LogClass()
@Injectable({
  providedIn: "root"
})
export class StepFacade {
  private logger: Logger;

  constructor(
    private circulationsService: AbstractCirculationsService,
    private stepService: StepService,
    private translationService: I18nService,
    private store: Store
  ) {}

  @LogMethod()
  public loadSteps(id: CirculationId): void {
    this.store.dispatch(StepAPIActions.loadStepsForCirculation({ id }));
  }

  @LogMethod()
  public delete(step: IStepInit): Observable<EntityId> {
    return this.circulationsService
      .DeleteStep(step)
      .pipe(
        tap(() => this.store.dispatch(StepActions.deleteStep({ id: step.id })))
      );
  }

  @LogMethod()
  public save(step: Step): Observable<EntityId> {
    return this.circulationsService.SaveStep(step);
  }

  @LogMethod()
  forward(data: IStepForwardData): Observable<void> {
    return of(data).pipe(
      this.logger.observableOperator("forward"),
      switchMap((d) => this.circulationsService.ForwardStep(d))
    );
  }

  @LogMethod()
  overrideForward(data: IStepOverrideForwardData): Observable<void> {
    return of(data).pipe(
      this.logger.observableOperator("overrideForward"),
      switchMap((d) => this.circulationsService.overrideForwardStep(d))
    );
  }

  @LogMethod()
  public getStepsForCirculation(id: CirculationId): Observable<Step[]> {
    return this.store.select(selectStepsOfCirculation(id));
  }

  @LogMethod()
  public getStep(stepId: StepId): Observable<Step> {
    return this.store
      .select(selectStep(stepId))
      .pipe(filter((s) => typeof s !== "undefined" && null !== s));
  }

  @LogMethod()
  public getMySteps(): Observable<IMyTask[]> {
    return this.circulationsService.GetMyTasks();
  }

  @LogMethod()
  public getCurrentStep(): Observable<Step> {
    return this.store.select(selectCurrentStep);
  }

  @LogMethod()
  public getCurrentStepType(): Observable<IStepTypeProviderDescription> {
    return this.getCurrentStep().pipe(
      map((step) => step.props.payload.stepType),
      switchMap((key) => this.getStepType(key))
    );
  }

  @LogMethod()
  public getStepType(
    stepTypeKey: string
  ): Observable<IStepTypeProviderDescription> {
    return this.addTranslation(of(this.stepService.getType(stepTypeKey)));
  }

  @LogMethod()
  public getTypes(): Observable<IStepTypeProviderDescription> {
    return this.addTranslation(from(this.stepService.getTypes()));
  }

  private addTranslation(
    types: Observable<IStepTypeProviderDescription>
  ): Observable<IStepTypeProviderDescription> {
    return types.pipe(
      mergeMap((p) =>
        forkJoin({
          provider: of(p),
          name: this.translationService.get(p.name),
          description: this.translationService.get(p.description)
        })
      ),
      map((x) => {
        x.provider.name = x.name;
        x.provider.description = x.description;
        return x.provider;
      })
    );
  }

  @LogMethod()
  public complete(step: Step, result: IStepResultData): Observable<void> {
    return this.circulationsService.CompleteStep(step, result);
  }

  @LogMethod()
  public reorder(reorderData: IReorderStepsData): Observable<CirculationId> {
    //NTH: Reorder should be a NGRX Action
    return this.circulationsService
      .ReorderSteps(reorderData)
      .pipe(tap((circId) => this.loadSteps(circId)));
  }
}
